// kfe-lyrics-display.js — bilingual scrolling lyric strip for music tracks.
//
// Ported from rhythmic mandarin web's `src/ui/rm-lyrics-display.js`. Two
// adaptations:
//   • English-first language order (KFE pedagogy is English-target, opposite
//     of rhythmic mandarin which is Mandarin-target).
//   • Tap-to-seek: lines are clickable and emit a `lineSeek` CustomEvent
//     with `{detail: {index, playtime}}` so the parent can `audio.currentTime`
//     to that line's start. rm-lyrics-display didn't have this; brief 012
//     success criterion #4 requires it.
//
// Renders all lines up-front and lets the parent set `activeIndex` to drive
// highlighting. The parent is also responsible for scroll-into-view (see
// `<kfe-music-track>`); we expose the per-line `data-i` attribute for that.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";

/** @typedef {import("../engines/lyrics-sync.js").LyricLine} LyricLine */

export class KFELyricsDisplay extends KFEComponent {
  static props = ["lines", "activeIndex"];

  static styles = `
    :host { display: block; }
    .line {
      padding: 0.6rem 0.75rem;
      margin: 0.15rem 0;
      opacity: 0.4;
      border-left: 3px solid transparent;
      border-radius: 0.25rem;
      cursor: pointer;
      transition: opacity 150ms ease, background 150ms ease, border-color 150ms ease;
    }
    .line:hover { opacity: 0.85; background: var(--kfe-surface-hover); }
    .line.active {
      opacity: 1;
      background: rgba(248, 155, 37, 0.14);
      border-left-color: var(--kfe-accent);
    }
    .line.active .en { color: var(--kfe-accent); font-weight: 600; }
    .line.active .zh { color: var(--kfe-fg); }
    .en { font-size: 1.1rem; color: var(--kfe-fg); line-height: 1.35; }
    .zh { font-size: 0.95rem; color: var(--kfe-muted); line-height: 1.35; margin-top: 0.15rem; }
    .empty { color: var(--kfe-muted); padding: 0.75rem; }
  `;

  render() {
    /** @type {LyricLine[] | null} */
    const lines = /** @type {any} */ (this).lines;
    const active = Number(/** @type {any} */ (this).activeIndex ?? -1);

    if (!Array.isArray(lines) || lines.length === 0) {
      return html`<p class="empty">No lyrics for this track.</p>`;
    }

    const root = document.createDocumentFragment();
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      const el = document.createElement("div");
      el.className = "line" + (i === active ? " active" : "");
      el.setAttribute("data-i", String(i));
      el.setAttribute("role", "button");
      el.setAttribute("tabindex", "0");

      const en = document.createElement("div");
      en.className = "en";
      en.textContent = l.lyricEn || "";
      el.appendChild(en);

      // Only render the zh row if there's something to show — some lines
      // (instrumental breaks etc.) may be English-only.
      if (l.lyricZh) {
        const zh = document.createElement("div");
        zh.className = "zh";
        zh.textContent = l.lyricZh;
        el.appendChild(zh);
      }

      const playtime = Number(l.playtime) || 0;
      el.addEventListener("click", () => this.emit("lineSeek", { index: i, playtime }));
      el.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.emit("lineSeek", { index: i, playtime });
        }
      });
      root.appendChild(el);
    }
    return /** @type {DocumentFragment} */ (/** @type {unknown} */ (root));
  }
}
